import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Bell, BellOff } from "lucide-react";
import { useWS } from "@/context/WSContext";
import { useAuth } from "@/context/AuthContext";

/**
 * Foreground Web Push: when the user has granted Notifications permission and
 * the tab is hidden, mirror our WS events (`tip_received`, `notification_new`)
 * into OS-level notifications so creators are pulled back into the app.
 * No service worker / VAPID needed — this uses the plain Notifications API,
 * which fires as long as the tab is loaded (even in a background tab).
 */

const NOTIFY_ICON = "/favicon.ico";

const bodyFor = (evt) => {
  if (evt.type === "tip_received") {
    const amt = (evt.amount_cents / 100).toFixed(2);
    return {
      title: `⚡ ${evt.tipper_name || "Someone"} boosted your video`,
      body: `+$${amt}${evt.message ? ` — “${evt.message}”` : ""}`,
      tag: `tip:${evt.tip_id}`,
      url: evt.video_id ? `/v/${evt.video_id}` : "/notifications",
    };
  }
  if (evt.type === "notification_new" && evt.notification) {
    const n = evt.notification;
    if (n.kind === "follow") {
      return {
        title: "New follower on MatrixShopping",
        body: `${n.actor_name || "Someone"} started following you`,
        tag: `follow:${n.id}`,
        url: n.actor_name ? `/u/${n.actor_name}` : "/notifications",
      };
    }
    if (n.kind === "comment") {
      return {
        title: `💬 ${n.actor_name || "Someone"} commented`,
        body: n.message ? `“${n.message}”` : "on your video",
        tag: `comment:${n.id}`,
        url: n.video_id ? `/v/${n.video_id}` : "/notifications",
      };
    }
  }
  return null;
};

export const useNotificationPermission = () => {
  const supported = typeof window !== "undefined" && "Notification" in window;
  const [permission, setPermission] = useState(
    supported ? Notification.permission : "unsupported"
  );

  const request = useCallback(async () => {
    if (!supported) return "unsupported";
    if (Notification.permission !== "default") {
      setPermission(Notification.permission);
      return Notification.permission;
    }
    const p = await Notification.requestPermission();
    setPermission(p);
    return p;
  }, [supported]);

  return { supported, permission, request };
};

export default function WebPushBridge() {
  const { subscribe } = useWS();
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return undefined;
    if (typeof window === "undefined" || !("Notification" in window)) {
      return undefined;
    }

    return subscribe((evt) => {
      if (Notification.permission !== "granted") return;
      if (!document.hidden) return; // when tab is visible, our in-app toast is enough
      const meta = bodyFor(evt);
      if (!meta) return;
      try {
        const n = new Notification(meta.title, {
          body: meta.body,
          icon: NOTIFY_ICON,
          tag: meta.tag,
          renotify: true,
        });
        n.onclick = () => {
          window.focus();
          if (meta.url) window.location.assign(meta.url);
          n.close();
        };
      } catch {
        /* browser may throw if the page is being unloaded — swallow */
      }
    });
  }, [subscribe, user]);

  return null;
}

/**
 * Small in-page banner asking the user to enable OS notifications.
 * Auto-hides once permission is `granted` or `denied`.
 * Mount at the top of the /notifications page.
 */
export function EnablePushBanner() {
  const { supported, permission, request } = useNotificationPermission();
  const [dismissed, setDismissed] = useState(false);

  if (!supported || permission !== "default" || dismissed) return null;

  const handleEnable = async () => {
    const p = await request();
    if (p === "granted") {
      toast.success("Push notifications enabled — you'll get pinged even when MatrixShopping is in the background.");
    } else if (p === "denied") {
      toast("Notifications blocked. You can re-enable them in your browser settings.");
    }
  };

  return (
    <div
      data-testid="enable-push-banner"
      className="mb-6 flex items-center justify-between gap-3 p-4 bg-[#0A0A0C] border border-[#39FF14]/40 shadow-[0_0_20px_rgba(57,255,20,0.15)]"
    >
      <div className="flex items-center gap-3 min-w-0">
        <Bell className="h-5 w-5 text-[#39FF14] flex-shrink-0" strokeWidth={1.8} />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-white">
            Never miss a boost
          </p>
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/50 mt-0.5">
            // enable browser push · works when the tab is backgrounded
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          data-testid="enable-push-btn"
          onClick={handleEnable}
          className="bg-[#39FF14] text-black font-bold uppercase tracking-widest text-[10px] px-4 py-2 hover:shadow-[0_0_15px_rgba(57,255,20,0.5)]"
        >
          enable
        </button>
        <button
          data-testid="dismiss-push-banner"
          onClick={() => setDismissed(true)}
          aria-label="dismiss"
          className="h-8 w-8 flex items-center justify-center text-white/40 hover:text-white/70"
        >
          <BellOff className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
